import {
  Input as HeadlessInput,
  type InputProps as HeadlessInputProps,
} from "@headlessui/react";
import { type VariantProps, cva } from "class-variance-authority";
import { type ReactNode, forwardRef } from "react";
import { classNames } from "../../utils/classNames";

const dateTypes = ["date", "datetime-local", "month", "time", "week"];
type DateType = (typeof dateTypes)[number];

const inputContainerVariants = cva(
  [
    "relative block w-full group",

    // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
    "before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-form-primary",

    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    "dark:before:hidden",

    // Focus ring
    "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:after:focus-within:ring-2 sm:after:focus-within:ring-focus",

    // Disabled state
    "has-[[data-disabled]]:opacity-50 before:has-[[data-disabled]]:bg-zinc-950/5 before:has-[[data-disabled]]:shadow-none",

    // Invalid state
    "before:has-[[data-invalid]]:shadow-error/10",

    // Icon
    "[&>[data-slot=icon]]:pointer-events-none [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-secondary",
    "[&>[data-slot=icon]]:absolute [&>[data-slot=icon]]:left-[calc(theme(spacing[3])-1px)] [&>[data-slot=icon]]:inset-y-0",

    // Avatar
    "[&>[data-slot=avatar]]:pointer-events-none [&>[data-slot=avatar]]:size-6 [&>[data-slot=avatar]]:shrink-0 [&>[data-slot=avatar]]:text-inverse-primary",
    "[&>[data-slot=avatar]]:absolute [&>[data-slot=avatar]]:left-[calc(theme(spacing[3])-1px)] [&>[data-slot=avatar]]:inset-y-0 [&>[data-slot=avatar]]:my-1.5",
    "[&>[data-slot=avatar]+input]:pl-10",

    // Flag
    "[&>[data-slot=flag]]:pointer-events-none [&>[data-slot=flag]]:size-6 [&>[data-slot=flag]]:shrink-0 [&>[data-slot=flag]]:text-inverse-primary",
    "[&>[data-slot=flag]]:absolute [&>[data-slot=flag]]:left-[calc(theme(spacing[3])-1px)] [&>[data-slot=flag]]:inset-y-0 [&>[data-slot=flag]]:my-1.5",
    "[&>[data-slot=flag]+input]:pl-10",

    // Read-only state
    "data-[readonly=true]:pointer-events-none data-[readonly=true]:opacity-75 data-[readonly=true]:bg-zinc-950/[0.025] before:data-[readonly=true]:shadow-none",
  ],
  {
    defaultVariants: {
      size: "md",
    },
    variants: {
      size: {
        md: "[&>[data-slot=icon]]:size-4 [&>[data-slot=icon]]:my-2.5",
        lg: "[&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:my-3.5",
      },
    },
  },
);

const inputVariants = cva(
  [
    // Basic layout
    "relative block w-full appearance-none rounded-lg",

    // Typography
    "text-sm/6 text-primary placeholder:text-tertiary",

    // Border
    "border border-inverse-tertiary/10 data-[hover]:border-inverse-tertiary/20",

    // Background color
    "bg-transparent dark:bg-form-primary",

    // Hide default focus styles
    "focus:outline-none",

    // Invalid state
    "data-[invalid]:border-error data-[invalid]:data-[hover]:border-error data-[invalid]:dark:border-error data-[invalid]:data-[hover]:dark:border-error",

    // Disabled state
    "data-[disabled]:border-inverse-tertiary/20 dark:data-[hover]:data-[disabled]:border-tertiary/15",

    // Read-only state
    "read-only:cursor-default read-only:hover:border-inverse-tertiary/10",
  ],
  {
    defaultVariants: {
      icon: false,
      size: "md",
    },
    variants: {
      icon: {
        true: "",
      },
      size: {
        md: "py-[calc(theme(spacing[1.5])-1px)] px-[calc(theme(spacing[3])-1px)]",
        lg: "py-[calc(theme(spacing[3])-1px)] px-[calc(theme(spacing[3])-1px)] text-base/6",
      },
    },
    compoundVariants: [
      {
        icon: true,
        size: "md",
        className: "pl-[calc(theme(spacing[8])-1px)]",
      },
      {
        icon: true,
        size: "lg",
        className: "pl-[calc(theme(spacing[9])-1px)]",
      },
    ],
  },
);

type InputProps = Omit<VariantProps<typeof inputVariants>, "icon"> &
  Omit<HeadlessInputProps, "size"> & {
    controlClasses?: string;
    icon?: ReactNode;
    type?:
      | "email"
      | "number"
      | "password"
      | "search"
      | "tel"
      | "text"
      | "url"
      | DateType;
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, icon, size, controlClasses, style, ...props },
  ref,
) {
  return (
    <span
      data-slot="control"
      data-readonly={props.readOnly}
      className={inputContainerVariants({ size, className })}
      style={style}
    >
      {icon}
      <HeadlessInput
        ref={ref}
        invalid={!!props["aria-invalid"]}
        className={classNames(
          inputVariants({ icon: !!icon, size, className: controlClasses }),
          [
            // Date classes
            props.type &&
              dateTypes.includes(props.type) && [
                "[&::-webkit-datetime-edit-fields-wrapper]:p-0",
                "[&::-webkit-date-and-time-value]:min-h-[1.5em]",
                "[&::-webkit-datetime-edit]:inline-flex",
                "[&::-webkit-datetime-edit]:p-0",
                "[&::-webkit-datetime-edit-year-field]:p-0",
                "[&::-webkit-datetime-edit-month-field]:p-0",
                "[&::-webkit-datetime-edit-day-field]:p-0",
                "[&::-webkit-datetime-edit-hour-field]:p-0",
                "[&::-webkit-datetime-edit-minute-field]:p-0",
                "[&::-webkit-datetime-edit-second-field]:p-0",
                "[&::-webkit-datetime-edit-millisecond-field]:p-0",
                "[&::-webkit-datetime-edit-meridiem-field]:p-0",
              ],
          ],
        )}
        {...props}
      />
    </span>
  );
});
